import * as QueryKeys from "data";

import {
  Alert,
  AlertTitle,
  Box,
  Card,
  CardContent,
  CardHeader,
  FormGroup,
} from "@mui/material";
import { useIdentity } from "contexts/identity-context";

import { makeStyles } from "@mui/styles";
import { fetchDatasets, fetchDefinitions } from "data/queries";
import { useEffect, useState } from "react";
import { UseQueryResult, useQuery } from "@tanstack/react-query";
import { resolveIdentityId } from "utils/identity";
import { customColumns, customersToRows } from "./columns-rows";

import {
  DatasetDefinitions,
  DatasetFiltersType,
  DatasetList,
} from "data/models";
import { useLocation } from "react-router-dom";

import { GridRowClassNameParams } from "@mui/x-data-grid-premium";
import { ReactQueryErrorWrapper } from "components/shared/react-query-error-wrapper";
import CustomServerPaginatedTable from "components/shared/server-paginated-table";
import { useCustomSnackbars } from "components/snackbars/useCustomSnackbars";
import * as localStorageKeys from "data/local-storage-keys";
import usePaginatedTableQuery from "shared-hooks/use-paginated-table-query";
import FilterComponent from "../share-component/filter-component";
import { updateQueryAndFilters } from "../utils";
import { DatasetFilters } from "./filters";

export const DownloadPage = () => {
  const [identityState] = useIdentity();

  const identityId = resolveIdentityId(identityState);

  const urlLocationQuery = useLocation().search;

  const savedSearch = localStorage.getItem(localStorageKeys.searchDatasets);
  const { enqueueLinkCorruptedSnackbar } = useCustomSnackbars();

  let initialQueryFilterState: DatasetFiltersType = {
    query: "",
    product: [],
    region: [],
    format: [],
    datum: [],
    release: "",
    latestRelease: false,
    licenced: false,
    includeChildProducts: true,
  };

  const [queryFilterState, setQueryFilterState] = useState<DatasetFiltersType>(
    initialQueryFilterState,
  );
  const [loadingFilters, setLoadingFilters] = useState<boolean>(false);

  const { paginationModel, handleChangePaginationModel, searchQuery } =
    usePaginatedTableQuery<DatasetList>(
      (limit: number, offset: number) => {
        return fetchDatasets(queryFilterState, limit, offset);
      },
      QueryKeys.datasetsSearchQuery,
      identityId,
      queryFilterState,
    );

  const datasetDefinitions: UseQueryResult<DatasetDefinitions, unknown> =
    useQuery({
      queryKey: [QueryKeys.definitionsKey],
      queryFn: () => fetchDefinitions(),
    });

  useEffect(() => {
    if (!datasetDefinitions.isSuccess) {
      return;
    }
    updateQueryAndFilters({
      savedSearch,
      setQueryFilterState,
      setLoadingFilters,
      urlLocationQuery,
      initialQueryFilterState,
      definitions: datasetDefinitions,
      enqueueLinkCorruptedSnackbar,
    });
  }, [datasetDefinitions.isSuccess]);

  const useStyles = makeStyles({
    highlightedRow: {
      color: "grey",
    },
  });

  const classes = useStyles();

  const getCustomRowLicence = (params: GridRowClassNameParams): string => {
    if (params.row.licenced == false) {
      return classes.highlightedRow;
    }
    return "";
  };

  const customColumns_ = customColumns();

  const isLoading = searchQuery.isPending;

  const rows = searchQuery.data ? customersToRows(searchQuery.data) : [];

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          gap: "16px",
          position: "relative",
        }}
      >
        <ReactQueryErrorWrapper
          queries={[searchQuery, datasetDefinitions]}
          mutations={[]}
        />
        <Alert severity="info" variant="outlined">
          <AlertTitle>
            <b>Changes for National Buildings 4.0 and Insight Packs</b>
          </AlertTitle>
          In preparation for the <b>March National Buildings V4.0 launch</b>, we
          have introduced <b>Insight Packs</b>. This update adds a dedicated{" "}
          <b>Insight Pack toggle</b> and includes, Height, Roof and Solar
          Insight Packs + Pool Insight Pack (located under ‘Address – Pool’).
          All Insight Packs can be easily managed using the standard product
          filter and the toggle.
        </Alert>
        <Card>
          <CardContent
            style={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
            }}
          >
            <FormGroup style={{ display: "flex", flexDirection: "row" }}>
              {loadingFilters ? (
                <Box
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "16px",
                  }}
                >
                  <h2>Loading...</h2>
                </Box>
              ) : (
                <FilterComponent
                  queryFilterState={queryFilterState}
                  setQueryFilterState={setQueryFilterState}
                  initialQueryFilterState={initialQueryFilterState}
                  renderFilter={DatasetFilters}
                  definitions={datasetDefinitions}
                  localStorageKey={localStorageKeys.searchDatasets}
                />
              )}
            </FormGroup>
          </CardContent>
        </Card>

        <CustomServerPaginatedTable
          rowCount={searchQuery.data?.links?.numberMatched ?? -1}
          rows={rows}
          getCustomRowClass={getCustomRowLicence}
          paginationModel={paginationModel}
          setPaginationModel={handleChangePaginationModel}
          cols={customColumns_}
          isLoading={isLoading}
          overlayText={
            "We couldn't find any datasets that match your filter criteria."
          }
          disableColumnMenu
          disableColumnSelector
        />
      </div>
    </>
  );
};
